import { useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface Projeto {
  id: number
  nome: string
  codigo_projeto?: string | null
}

interface SessaoCapturaHeaderProps {
  sessao: {
    contexto?: string
    projeto_id?: number | null
  } | null
  seconds: number
  projetoNome?: string | null
  projetoCodigo?: string | null
  projetos?: Projeto[]
  onChangeProjeto?: (id: number | null) => void
  onMinimize: () => void
  onOpenDocs: () => void
}

interface CtxStyle {
  label: string
  bg: string
  color: string
}

const CTX_CONFIG: Record<string, CtxStyle> = {
  OBRA:    { label: 'OBRA',    bg: 'rgba(200,168,75,0.15)',  color: '#C8A84B' },
  REUNIAO: { label: 'REUNIÃO', bg: 'rgba(196,212,74,0.12)',  color: 'var(--color-hero)' },
  LIVRE:   { label: 'LIVRE',   bg: 'rgba(255,255,255,0.06)', color: '#999' },
}

function fmtTime(s: number): string {
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
  return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
}

export default function SessaoCapturaHeader({ sessao, seconds, projetoNome, projetoCodigo, projetos, onChangeProjeto, onMinimize, onOpenDocs }: SessaoCapturaHeaderProps) {
  const ctx = CTX_CONFIG[sessao?.contexto] ?? CTX_CONFIG.LIVRE
  const hasProject = !!sessao?.projeto_id
  const [open, setOpen] = useState(false)

  const handleSelect = (id: number | null) => {
    setOpen(false)
    onChangeProjeto?.(id)
  }

  return (
    <div
      className="flex-shrink-0 flex flex-col gap-2 px-4 pt-4 pb-3"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
    >
      {/* Linha 1 — indicador + título + timer + minimize */}
      <div className="flex items-center gap-2">
        <span
          className="w-2 h-2 rounded-full animate-pulse shrink-0"
          style={{ background: 'var(--color-vermelho)' }}
        />

        <div className="flex-1 min-w-0">
          <span className="text-[13px] font-semibold text-[#f0f0f0]">Sessão de captura</span>
        </div>

        <span
          className="text-[18px] font-light text-[#f0f0f0] shrink-0"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {fmtTime(seconds)}
        </span>

        {hasProject && (
          <button
            onClick={onOpenDocs}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] text-[#888] hover:text-[#f0f0f0] transition-colors border-none bg-transparent cursor-pointer"
            title="Documentos do projeto"
          >
            <span className="material-symbols-outlined text-[15px]">folder_open</span>
          </button>
        )}

        <button
          onClick={onMinimize}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-[#666] hover:text-[#f0f0f0] hover:bg-white/[0.06] transition-colors border-none bg-transparent cursor-pointer shrink-0"
          aria-label="Minimizar"
        >
          <span className="material-symbols-outlined text-lg">keyboard_arrow_down</span>
        </button>
      </div>

      {/* Linha 2 — projeto (combobox) + contexto + GPS */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Seletor de projeto */}
        {onChangeProjeto && projetos ? (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border-none cursor-pointer transition-colors min-w-0"
                style={{ background: 'rgba(255,255,255,0.07)', color: projetoNome ? '#ccc' : '#666' }}
                title="Trocar projeto"
              >
                <span className="material-symbols-outlined text-[13px] shrink-0">work</span>
                <span className="truncate max-w-[140px]">
                  {projetoNome ? (projetoCodigo ? `${projetoCodigo} · ${projetoNome}` : projetoNome) : 'Sem projeto'}
                </span>
                <span className="material-symbols-outlined text-[11px] text-[#555] shrink-0">expand_more</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-1" align="start">
              <button
                onClick={() => handleSelect(null)}
                className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-[#888] rounded-lg border-none bg-transparent cursor-pointer hover:bg-white/[0.06] transition-colors text-left"
              >
                <span className="material-symbols-outlined text-[14px]">remove_circle_outline</span>
                Sem projeto
              </button>
              {projetos.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleSelect(p.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[12px] rounded-lg border-none bg-transparent cursor-pointer hover:bg-white/[0.06] transition-colors text-left"
                  style={{ color: sessao?.projeto_id === p.id ? 'var(--color-hero)' : '#ddd' }}
                >
                  {sessao?.projeto_id === p.id && (
                    <span className="material-symbols-outlined text-[14px]" style={{ color: 'var(--color-hero)' }}>check</span>
                  )}
                  {sessao?.projeto_id !== p.id && <span className="w-[14px] shrink-0" />}
                  <span className="truncate">
                    {p.codigo_projeto ? `${p.codigo_projeto} · ` : ''}{p.nome}
                  </span>
                </button>
              ))}
            </PopoverContent>
          </Popover>
        ) : projetoNome ? (
          <span className="text-[11px] text-[#666] truncate">
            {projetoCodigo ? `${projetoCodigo} · ` : ''}{projetoNome}
          </span>
        ) : null}

        {/* Chip de contexto */}
        <span
          className="text-[10px] uppercase tracking-[0.08em] font-bold px-2 py-0.5 rounded-full shrink-0"
          style={{ background: ctx.bg, color: ctx.color }}
        >
          {ctx.label}
        </span>

        {/* GPS indicator */}
        <div className="flex items-center gap-1 ml-auto shrink-0">
          <span className="material-symbols-outlined" style={{ fontSize: 12, color: 'var(--color-verde)' }}>location_on</span>
          <span className="text-[10px]" style={{ color: 'var(--color-verde)' }}>GPS</span>
        </div>
      </div>
    </div>
  )
}
